import React from 'react';
import { format } from 'date-fns';
import {
  AttachMoney,
  CalendarMonth,
  AttachFile,
  GetApp,
} from '@mui/icons-material';
import {
  BudgetRequest,
  BudgetRequestRecipientStatus,
  BudgetRequestRecipient,
} from '@models/budget.model';
import { useTranslation } from '@hooks/useTranslation';

interface BudgetRequestDetailsProps {
  budgetRequest: BudgetRequest;
  activeSubTab: 'received' | 'delegated';
  isDelegatedTask: boolean;
}

export const BudgetRequestDetails: React.FC<BudgetRequestDetailsProps> = ({
  budgetRequest,
  activeSubTab,
  isDelegatedTask,
}) => {
  const tTaskDetail = useTranslation('taskDetail');

  const formatCurrency = (amount?: number): string => {
    if (!amount) return tTaskDetail.budget.notSpecified;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (date?: string | Date) => {
    if (!date) return '-';
    return format(new Date(date), 'MMM dd, yyyy');
  };

  const acceptedRecipient = budgetRequest.recipients?.find(
    (recipient: BudgetRequestRecipient) =>
      recipient.status === BudgetRequestRecipientStatus.ACCEPTED,
  );

  return (
    <div className="bg-white border border-gray-200 rounded-lg">
      <div className="p-4 border-b border-gray-200">
        <h3 className="text-lg font-medium text-gray-900">
          {activeSubTab === 'delegated'
            ? !isDelegatedTask
              ? tTaskDetail.budget.delegationBidDetailsTitle
              : tTaskDetail.budget.subDelegationBidDetailsTitle
            : activeSubTab === 'received' && isDelegatedTask
              ? tTaskDetail.budget.originalBidDetailsTitle
              : tTaskDetail.budget.bidDetailsTitle}
        </h3>
      </div>

      <div className="p-4 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {tTaskDetail.budget.amountLabel}
            </label>
            <div className="flex items-center gap-2">
              <AttachMoney className="h-4 w-4 text-gray-500" />
              <span className="text-lg font-semibold text-green-600">
                {formatCurrency(budgetRequest.maxBudgetAmount)}
              </span>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {tTaskDetail.budget.startDateLabel}
            </label>
            <div className="flex items-center gap-2">
              <CalendarMonth className="h-4 w-4 text-gray-500" />
              <span className="text-sm text-gray-900">
                {formatDate(budgetRequest.requestedStartDate)}
              </span>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {tTaskDetail.budget.endDateLabel}
            </label>
            <div className="flex items-center gap-2">
              <CalendarMonth className="h-4 w-4 text-gray-500" />
              <span className="text-sm text-gray-900">
                {formatDate(budgetRequest.requestedEndDate)}
              </span>
            </div>
          </div>

          {acceptedRecipient?.respondedAt && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {activeSubTab === 'delegated'
                  ? !isDelegatedTask
                    ? tTaskDetail.budget.delegationDate
                    : tTaskDetail.budget.subDelegationDate
                  : tTaskDetail.budget.acceptedDate}
              </label>
              <div className="flex items-center gap-2">
                <CalendarMonth className="h-4 w-4 text-gray-500" />
                <span className="text-sm text-gray-900">
                  {formatDate(acceptedRecipient?.respondedAt)}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Description */}
        {budgetRequest.description && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {tTaskDetail.budget.description}
            </label>
            <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded">
              {budgetRequest.description}
            </p>
          </div>
        )}

        {/* Documents */}
        {(budgetRequest.documents?.length ?? 0) > 0 && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {tTaskDetail.budget.attachedDocuments}
            </label>
            <div className="space-y-2">
              {budgetRequest.documents?.map((doc, index) => (
                <div
                  key={`request-${index}`}
                  className="flex items-center gap-3 p-2 bg-green-50 rounded border border-green-200 cursor-pointer hover:bg-green-100"
                  onClick={() => doc.url && window.open(doc.url, '_blank')}>
                  <AttachFile className="h-4 w-4 text-green-600" />
                  <span className="flex-1 text-sm text-gray-700">
                    {doc.name}
                  </span>
                  <span className="text-xs text-green-600">
                    {activeSubTab === 'delegated'
                      ? !isDelegatedTask
                        ? tTaskDetail.budget.delegationLabel
                        : tTaskDetail.budget.subDelegationLabel
                      : tTaskDetail.budget.budgetRequestLabel}
                  </span>
                  <GetApp className="h-4 w-4 text-green-600" />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};